import React from 'react';
import {useTranslation} from 'react-i18next';
import {UNIT_DAYS} from '../systemRulesModel';

/**
 * Los campos de los umbrales de una sensibilidad: Malo y Crítico (o Advertencia y Crítico en días, el 1.º y el
 * 2.º aviso de inversión) o uno solo (Hoy, «deja afuera»). Salió del panel de una regla de Dashbo
 * (`SystemRulePanel`) para que la tuerca del Tablero edite con los MISMOS campos (Centro de Alertas v2,
 * README §4.4): una sola forma de escribir un umbral, con la misma validación (`validateThresholds`).
 *
 * - `fields`: `[{level, unit, optional}]` (`thresholdFields` de `systemRulesModel`).
 * - `values` / `errors`: por nivel; el error es una clave i18n.
 * - `labelFor(level)`: el rótulo ya traducido.
 * - `idPrefix`: dos instancias en la misma pantalla (el panel y la tuerca) no pueden repetir ids.
 * - `readOnly`: se leen y no se escriben.
 *
 * El valor es HUMANO (15 = 15 %, 7 = 7 días). Va con las clases `.alc-threshold*` de `_alerts-center.scss`,
 * que cuelgan de `.ui-v2`: la tuerca va por portal a `body`, que en la 2.1 lleva esa clase.
 */
export function ThresholdFields({fields, values, errors = {}, labelFor, idPrefix = 'alc-threshold', readOnly = false,
                                    onChange}) {
    const {t} = useTranslation();
    const unitText = (unit) => (unit === UNIT_DAYS ? t('alerts_center.rules.days_unit') : '%');
    return (
        <div className="alc-thresholds">
            {fields.map((field) => {
                const inputId = `${idPrefix}-${field.level}`;
                const errorKey = errors[field.level];
                return (
                    <div key={field.level} className={`alc-threshold -${field.level.toLowerCase()}${errorKey ? ' -error' : ''}`}>
                        <label htmlFor={inputId} className="alc-threshold__label">
                            <span className="alc-threshold__dot" aria-hidden="true"/>
                            {labelFor(field.level)}
                        </label>
                        <div className="alc-threshold__field">
                            <input id={inputId} type="text" inputMode="decimal" autoComplete="off"
                                   value={values[field.level] === undefined || values[field.level] === null ? '' : values[field.level]}
                                   aria-invalid={errorKey ? 'true' : 'false'}
                                   aria-describedby={errorKey ? `${inputId}-error` : undefined}
                                   placeholder={field.optional ? t('alerts_center.rules.threshold_optional') : ''}
                                   readOnly={readOnly}
                                   onChange={(event) => onChange(field.level, event.target.value)}/>
                            <span className="alc-threshold__unit">{unitText(field.unit)}</span>
                        </div>
                        {errorKey && (
                            <span id={`${inputId}-error`} className="alc-field-error">{t(errorKey)}</span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default ThresholdFields;
